// Sugar Alarm — Cycle 457 (operator, second round): a meal photo is made
// small ON THE DEVICE before it is uploaded.
//
// A phone camera photo is several megabytes; the carb estimate needs a
// legible plate, not 12 MP. Before upload the photo is decoded with its own
// orientation applied, drawn at most MEAL_PHOTO_MAX_EDGE px on its longest
// side and re-encoded as a JPEG — tens of kilobytes, and none of the
// original's bytes (EXIF, GPS) leave the device. HEIC goes through the
// shared crop helper first, so an iPhone photo works. ANY failure (an old
// WebView without createImageBitmap, a decode error) sends the ORIGINAL file:
// the server pipeline still re-encodes and strips it, and a meal photo is
// never blocked by this step. Hero pictures and member avatars already come
// out of the cropper at 512 px; backgrounds at 1920 px.

import { isHeicFile, toCroppableBlob } from "./croppableImage";

export const MEAL_PHOTO_MAX_EDGE = 1024;
const JPEG_QUALITY = 0.85;

export function scaledToFit(
  width: number,
  height: number,
  max: number,
): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= max) return { width, height };
  const k = max / long;
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

interface Decoded {
  width: number;
  height: number;
  close?: () => void;
}

export interface MealPhotoDeps {
  decode: (blob: Blob) => Promise<Decoded>;
  encode: (bitmap: Decoded, width: number, height: number) => Promise<Blob>;
  toCroppable?: (file: File) => Promise<Blob>;
}

const browserDeps: MealPhotoDeps = {
  decode: (blob) =>
    createImageBitmap(blob, { imageOrientation: "from-image" }) as Promise<Decoded>,
  encode: async (bitmap, width, height) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context");
    ctx.drawImage(bitmap as unknown as CanvasImageSource, 0, 0, width, height);
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    if (!out) throw new Error("encode failed");
    return out;
  },
};

/** The photo to upload for a meal estimate: at most 1024 px, a fresh JPEG;
 *  the original file on any failure. Never throws. */
export async function prepareMealPhoto(
  file: File,
  deps: MealPhotoDeps = browserDeps,
): Promise<File> {
  try {
    const source: Blob = isHeicFile(file)
      ? await (deps.toCroppable ?? toCroppableBlob)(file)
      : file;
    const bitmap = await deps.decode(source);
    try {
      const { width, height } = scaledToFit(bitmap.width, bitmap.height, MEAL_PHOTO_MAX_EDGE);
      const blob = await deps.encode(bitmap, width, height);
      return new File([blob], "meal.jpg", { type: "image/jpeg" });
    } finally {
      bitmap.close?.();
    }
  } catch {
    return file;
  }
}
